// EMRG (Emergent) Integration Utility
// Handles launching projects in Emergent with pre-generated prompts
// Future-proofed for deep linking and API integration

const EMRG_APP_URL = 'https://app.emergent.sh';
const EMRG_API_URL = 'https://api.emergent.sh'; // Future API endpoint

// Feature flags - enable when EMRG adds support
const FEATURES = {
    deepLinking: false,      // Set to true when EMRG supports URL parameters
    apiIntegration: false,   // Set to true when EMRG API is available
};

/**
 * Generate a comprehensive EMRG-optimized prompt from a blueprint
 * This is tailored specifically for Emergent's AI builder
 */
export const generateEmrgPrompt = (blueprint, idea) => {
    if (!blueprint) return '';
    
    const lines = [];
    
    // Clear, actionable opening
    lines.push(`Build a complete web application called "${blueprint.app_name}".`);
    lines.push('');
    lines.push('## App Concept');
    lines.push(blueprint.summary);
    lines.push('');
    lines.push(`Original idea: ${idea}`);
    lines.push('');
    
    // Core features (prioritized)
    lines.push('## Core Features to Implement');
    const coreFeatures = blueprint.features.filter(f => f.priority === 'core');
    const niceToHave = blueprint.features.filter(f => f.priority === 'nice-to-have');
    
    coreFeatures.forEach((feature, i) => {
        lines.push(`${i + 1}. **${feature.name}**: ${feature.description}`);
    });
    
    if (niceToHave.length > 0) {
        lines.push('');
        lines.push('### Nice-to-have features (if time permits):');
        niceToHave.forEach((feature, i) => {
            lines.push(`- ${feature.name}: ${feature.description}`);
        });
    }
    lines.push('');
    
    // Screens/Pages
    lines.push('## Screens / Pages');
    blueprint.screens.forEach((screen, i) => {
        lines.push(`${i + 1}. **${screen.name}**: ${screen.purpose}`);
        if (screen.components && screen.components.length > 0) {
            lines.push(`   Components: ${screen.components.join(', ')}`);
        }
    });
    lines.push('');
    
    // Database structure
    if (blueprint.database && blueprint.database.length > 0) {
        lines.push('## Database Structure');
        blueprint.database.forEach(table => {
            lines.push(`- **${table.name}**: ${table.fields.join(', ')}`);
            if (table.relationships) {
                lines.push(`  Relationships: ${table.relationships}`);
            }
        });
        lines.push('');
    }
    
    // API Endpoints
    if (blueprint.api_endpoints && blueprint.api_endpoints.length > 0) {
        lines.push('## API Endpoints');
        blueprint.api_endpoints.forEach(endpoint => {
            lines.push(`- \`${endpoint.method} ${endpoint.path}\`: ${endpoint.description}`);
        });
        lines.push('');
    }
    
    // Tech Stack recommendation
    lines.push('## Recommended Tech Stack');
    lines.push('- Frontend: React with Tailwind CSS and Shadcn UI');
    lines.push('- Backend: FastAPI (Python)');
    lines.push('- Database: MongoDB');
    lines.push('- Authentication: JWT-based auth if user accounts needed');
    lines.push('');
    
    // Build instructions
    lines.push('## Build Instructions');
    lines.push('1. Start with the core features and main screens');
    lines.push('2. Implement the database models and API endpoints');
    lines.push('3. Add authentication if the app requires user accounts');
    lines.push('4. Polish the UI with responsive design');
    lines.push('5. Add any nice-to-have features if time permits');
    lines.push('');
    
    // Closing
    lines.push('Please build this as a complete, working MVP with a clean, modern UI.');
    
    return lines.join('\n');
};

/**
 * Generate structured payload for EMRG API (future use)
 * This format is ready for when EMRG adds API support
 */
export const generateEmrgApiPayload = (blueprint, idea, sessionId) => {
    return {
        source: {
            app: 'vibe-coding-coach',
            version: '1.0',
            session_id: sessionId || `vcc-${Date.now()}`
        },
        project: {
            name: blueprint.app_name,
            description: blueprint.summary,
            prompt: generateEmrgPrompt(blueprint, idea)
        },
        blueprint: {
            app_name: blueprint.app_name,
            summary: blueprint.summary,
            features: blueprint.features,
            screens: blueprint.screens,
            database: blueprint.database,
            api_endpoints: blueprint.api_endpoints,
            tech_stack: {
                frontend: 'React with Tailwind CSS',
                backend: 'FastAPI (Python)',
                database: 'MongoDB'
            },
            build_instructions: [
                'Start with the core features and main screens',
                'Implement the database models and API endpoints',
                'Add authentication if the app requires user accounts',
                'Polish the UI with responsive design',
                'Add any nice-to-have features if time permits'
            ]
        },
        preferences: {
            auto_start: true,
            initial_phase: 'setup'
        }
    };
};

/**
 * Build deep link URL for EMRG (future use)
 * Ready to enable when EMRG adds URL parameter support
 */
export const buildEmrgDeepLink = (blueprint, idea) => {
    const prompt = generateEmrgPrompt(blueprint, idea);
    const encodedPrompt = encodeURIComponent(prompt);
    return `${EMRG_APP_URL}/new?prompt=${encodedPrompt}`;
};

/**
 * Create project via EMRG API (future use)
 * Ready to enable when EMRG adds API support
 */
export const createEmrgProject = async (blueprint, idea, authToken) => {
    if (!FEATURES.apiIntegration) {
        return { success: false, error: 'API integration not yet available' };
    }
    
    const payload = generateEmrgApiPayload(blueprint, idea);
    
    try {
        const response = await fetch(`${EMRG_API_URL}/v1/projects/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        return {
            success: true,
            projectUrl: data.redirect_url || data.project?.url,
            projectId: data.project?.id
        };
    } catch (error) {
        console.error('EMRG API error:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Launch EMRG with the blueprint prompt
 * Automatically uses the best available method:
 * 1. API integration (if available)
 * 2. Deep linking (if available)
 * 3. Copy + Open (fallback)
 */
export const launchEmrg = async (blueprint, idea, options = {}) => {
    const prompt = generateEmrgPrompt(blueprint, idea);
    
    // Phase 3: Try API integration first (when available)
    if (FEATURES.apiIntegration && options.authToken) {
        const apiResult = await createEmrgProject(blueprint, idea, options.authToken);
        if (apiResult.success) {
            window.open(apiResult.projectUrl, '_blank', 'noopener,noreferrer');
            return {
                success: true,
                method: 'api',
                projectUrl: apiResult.projectUrl,
                message: 'Project created! EMRG is opening with your blueprint ready.'
            };
        }
    }
    
    // Phase 2: Try deep linking (when available)
    if (FEATURES.deepLinking) {
        const deepLink = buildEmrgDeepLink(blueprint, idea);
        window.open(deepLink, '_blank', 'noopener,noreferrer');
        return {
            success: true,
            method: 'deeplink',
            message: 'EMRG is opening with your prompt pre-filled.'
        };
    }
    
    // Phase 1: Fallback to copy + open
    let copySuccess = false;
    try {
        await navigator.clipboard.writeText(prompt);
        copySuccess = true;
    } catch (err) {
        console.error('Failed to copy prompt:', err);
        // Fallback: try using execCommand
        try {
            const textArea = document.createElement('textarea');
            textArea.value = prompt;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            textArea.style.top = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            copySuccess = document.execCommand('copy');
            document.body.removeChild(textArea);
        } catch (fallbackErr) {
            console.error('Fallback copy also failed:', fallbackErr);
        }
    }
    
    // Open EMRG in new tab
    window.open(EMRG_APP_URL, '_blank', 'noopener,noreferrer');
    
    return { 
        success: true,
        method: 'copy',
        prompt,
        copySuccess,
        message: copySuccess 
            ? 'Your EMRG builder prompt has been copied. EMRG is opening in a new tab. Paste the prompt there to start building your app.'
            : 'EMRG is opening in a new tab. Use the Copy button on your blueprint, then paste in EMRG.'
    };
};

/**
 * Get the EMRG app URL
 */
export const getEmrgUrl = () => EMRG_APP_URL;

/**
 * Check current integration capabilities
 */
export const getIntegrationStatus = () => ({
    deepLinking: FEATURES.deepLinking,
    apiIntegration: FEATURES.apiIntegration,
    currentMethod: FEATURES.apiIntegration ? 'api' : FEATURES.deepLinking ? 'deeplink' : 'copy'
});

/**
 * Check if seamless integration is available (no copy/paste needed)
 */
export const isSeamlessIntegrationAvailable = () => 
    FEATURES.deepLinking || FEATURES.apiIntegration;
